import { axialHexDistance } from '../hexmap/coords';
import { getHexTileMapKey } from '../hexmap/model/keys';
import { axialNeighbors, getTile } from '../hexmap/model/map';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { BuildingDef, RuleSet } from '../rules/schema';
import { buildingLevel } from './levels';
import { isHidden } from './spies';
import { mintOrder, outsideTerritory, territoryOwners } from './territory';
import type { Building, Cell, EntityId, SimContext, SimState } from './types';

/**
 * Building footprints. A building stands on `pos`; one with rules `growth`
 * (farm, forestry, barracks) also spreads over extra tiles
 * (`Building.footprint`) of the land it needs as it levels up. Every footprint tile counts as the
 * building's: nothing else can be placed there, it isn't wild-gatherable,
 * units treat it as the building (containers), and the building can be hit
 * from next to any of its tiles. Pure and deterministic.
 */

/** Ticks between re-checks for room to grow (tiles freed later still get used). */
const GROWTH_RECHECK_TICKS = 50;

/** Every tile building `b` covers: `pos` first, then its extra tiles. */
function buildingCells(b: Building): Cell[] {
  return b.footprint ? [b.pos, ...b.footprint] : [b.pos];
}

/** Whether building `b` covers `cell`. */
function covers(b: Building, cell: Cell): boolean {
  return buildingCells(b).some((c) => c.q === cell.q && c.r === cell.r);
}

/** Hex distance from `cell` to the nearest tile of `target` (a unit or a building). */
function distanceTo(target: { pos: Cell; footprint?: Cell[] }, cell: Cell): number {
  let d = axialHexDistance(target.pos, cell);
  for (const c of target.footprint ?? []) d = Math.min(d, axialHexDistance(c, cell));
  return d;
}

/**
 * Tile → the building covering it (any tile of its footprint). On a clash
 * (shouldn't happen) the older building keeps the tile.
 */
function buildingIndex(state: SimState): Map<HexTileMapKey, Building> {
  const index = new Map<HexTileMapKey, Building>();
  const all = Object.values(state.buildings).sort(
    (a, b) => mintOrder(a.id) - mintOrder(b.id) || (a.id < b.id ? -1 : 1)
  );
  for (const b of all) {
    for (const c of buildingCells(b)) {
      const key = getHexTileMapKey(c);
      if (!index.has(key)) index.set(key, b);
    }
  }
  return index;
}

/** Extra tiles a `def` building may cover at `level` (0 without `growth`). */
function extraTilesAllowed(def: BuildingDef, level: number): number {
  const byLevel = def.growth?.extraTilesByLevel ?? [];
  if (byLevel.length === 0) return 0;
  return byLevel[Math.min(level, byLevel.length) - 1] ?? 0;
}

/**
 * What building `b`'s extra tiles add: worker slots (`slotsPerExtraTile`,
 * farm/forestry) or storage (`capacityPerExtraTile`, barracks); 0 without
 * `growth`.
 */
function growthBonus(
  rules: RuleSet,
  b: Building,
  per: 'slotsPerExtraTile' | 'capacityPerExtraTile'
): number {
  return (rules.buildings[b.type]?.growth?.[per] ?? 0) * (b.footprint?.length ?? 0);
}

/**
 * Tiles where a unit stands that couldn't stay if building `b` grew under it
 * (anything but a farm's or forestry's own gatherers, who'd just be inside).
 * Units passing through don't count: they settle wherever they stop.
 */
function unitBlocked(state: SimState, rules: RuleSet, b: Building): Set<HexTileMapKey> {
  const blocked = new Set<HexTileMapKey>();
  const provides = !!rules.buildings[b.type]?.provides;
  for (const u of Object.values(state.units)) {
    // A hidden unit (spies.ts) holds no tile: growing under it gives nothing away.
    if (u.inside || (u.path && u.path.length > 0) || isHidden(rules, u)) continue;
    if (provides && u.owner === b.owner && rules.units[u.type]?.actions.includes('gather')) {
      continue;
    }
    blocked.add(getHexTileMapKey(u.pos));
  }
  return blocked;
}

/**
 * Tiles building `b` could grow onto now, best first: next to its current
 * footprint, of a type its `placement.on` needs, in the territory of the
 * owner's city that holds `b` (none while `b` is outside its owner's
 * territory), with no building (or footprint) on them, nor (with `blocked`)
 * a unit that couldn't stay inside. Best: the most neighbours of that type,
 * then the tile key.
 */
function growthCandidates(
  state: SimState,
  ctx: SimContext,
  b: Building,
  owners: ReadonlyMap<HexTileMapKey, EntityId> = territoryOwners(state, ctx.rules),
  index: ReadonlyMap<HexTileMapKey, Building> = buildingIndex(state),
  blocked: ReadonlySet<HexTileMapKey> = new Set()
): Cell[] {
  const on = ctx.rules.buildings[b.type]?.placement?.on;
  const cityId = owners.get(getHexTileMapKey(b.pos));
  if (!on || cityId === undefined || state.buildings[cityId]?.owner !== b.owner) return [];
  const fits = (cell: Cell) => {
    const tile = getTile(ctx.terrain, cell);
    return !!tile && (on as readonly string[]).includes(tile.type);
  };
  const seen = new Set<HexTileMapKey>();
  const found: Array<{ cell: Cell; key: HexTileMapKey; score: number }> = [];
  for (const from of buildingCells(b)) {
    for (const cell of axialNeighbors(from)) {
      const key = getHexTileMapKey(cell);
      if (seen.has(key)) continue;
      seen.add(key);
      if (!fits(cell) || owners.get(key) !== cityId || index.has(key) || blocked.has(key)) continue;
      const score = axialNeighbors(cell).filter(fits).length;
      found.push({ cell, key, score });
    }
  }
  found.sort((x, y) => y.score - x.score || (x.key < y.key ? -1 : x.key > y.key ? 1 : 0));
  return found.map((f) => f.cell);
}

/**
 * Grows completed building `b` onto the best candidate tiles (skipping any a
 * unit stands on that couldn't stay inside), one at a time,
 * until it covers as many extra tiles as its level allows or runs out of room.
 */
function growBuilding(
  state: SimState,
  ctx: SimContext,
  b: Building,
  owners?: ReadonlyMap<HexTileMapKey, EntityId>
): void {
  const def = ctx.rules.buildings[b.type];
  if (!def || b.buildProgress < 1) return;
  const allowed = extraTilesAllowed(def, buildingLevel(b));
  if ((b.footprint?.length ?? 0) >= allowed) return;
  const territory = owners ?? territoryOwners(state, ctx.rules);
  const index = buildingIndex(state);
  const blocked = unitBlocked(state, ctx.rules, b);
  while ((b.footprint?.length ?? 0) < allowed) {
    const [best] = growthCandidates(state, ctx, b, territory, index, blocked);
    if (!best) return;
    b.footprint = [...(b.footprint ?? []), { q: best.q, r: best.r }];
    index.set(getHexTileMapKey(best), b);
  }
}

/** Grows every completed building that has room to (oldest first). */
function growAll(state: SimState, ctx: SimContext): void {
  let owners: ReadonlyMap<HexTileMapKey, EntityId> | undefined;
  const all = Object.values(state.buildings).sort(
    (a, b) => mintOrder(a.id) - mintOrder(b.id) || (a.id < b.id ? -1 : 1)
  );
  for (const b of all) {
    const def = ctx.rules.buildings[b.type];
    if (!def?.growth || b.buildProgress < 1) continue;
    if ((b.footprint?.length ?? 0) >= extraTilesAllowed(def, buildingLevel(b))) continue;
    owners ??= territoryOwners(state, ctx.rules);
    growBuilding(state, ctx, b, owners);
  }
}

/**
 * Releases extra footprint tiles that are no longer in their building's
 * owner's territory (a newer city took them, or their city fell); a
 * territory-bound building outside its owner's territory keeps none. Growth
 * takes land again once the territory comes back.
 */
function releaseOutside(state: SimState, ctx: SimContext): void {
  let owners: ReadonlyMap<HexTileMapKey, EntityId> | undefined;
  for (const b of Object.values(state.buildings)) {
    if (!b.footprint) continue;
    owners ??= territoryOwners(state, ctx.rules);
    const territory = owners;
    const kept = outsideTerritory(state, ctx.rules, b, territory)
      ? []
      : b.footprint.filter(
          (c) => state.buildings[territory.get(getHexTileMapKey(c)) ?? '']?.owner === b.owner
        );
    if (kept.length === b.footprint.length) continue;
    if (kept.length > 0) b.footprint = kept;
    else delete b.footprint;
  }
}

export {
  buildingCells,
  buildingIndex,
  covers,
  distanceTo,
  extraTilesAllowed,
  GROWTH_RECHECK_TICKS,
  growAll,
  growBuilding,
  growthBonus,
  growthCandidates,
  releaseOutside,
};
